import { useDispatch, useStore } from 'react-redux';
import requestRestfulApi from '../Helpers/Request.helper';
import { NeoState } from '../store';
import { AxiosRequestHeaders, AxiosResponse, CancelToken } from 'axios';
import { IErrorResponse } from '../Models/DTO/Error.models';
import { useTranslation } from 'react-i18next';

export type IMethod =
  | 'GET'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'LINK'
  | 'UNLINK';

export interface IRequestOptions {
  url: string;
  apiVersion?: string;
  data?: any;
  method?: IMethod;
  query?: Record<string, boolean | string | number | undefined>;
  cancelToken?: CancelToken;
  actionName?: string;
  headers?: AxiosRequestHeaders;
  customToken?: string;
}

export interface IBulkRequest {
  requestId: string;
  method: IMethod;
  path: string;
  query: any;
}

export type IRequest = <T>({
  url,
  apiVersion,
  method,
  data,
  cancelToken,
  customToken,
  actionName,
  query,
  headers,
}: IRequestOptions) => Promise<AxiosResponse<T>>;

export type IRequestWithCallBack = <T>(
  options: IRequestOptions,
  data?: ((data: T) => void) | undefined,
  fail?: ((error: IErrorResponse) => void) | undefined,
) => Promise<void>;

export const useRequest = () => {
  const dispatch = useDispatch();
  const store = useStore<NeoState>();
  const { t } = useTranslation();

  const request = async <T>({
    url,
    apiVersion,
    method = 'GET',
    data,
    cancelToken,
    actionName,
    query,
    headers,
    customToken,
  }: IRequestOptions) =>
    requestRestfulApi<T>({
      url,
      customToken,
      apiVersion,
      cancelToken,
      actionName,
      dispatch,
      state: store.getState(),
      query,
      config: {
        method,
        data,
        headers,
      },
    });

  const requestWithCallback = async <T>(
    options: IRequestOptions,
    data?: (data: T) => void,
    fail?: (error: IErrorResponse) => void,
  ) => {
    const response = await request<T>(options);
    if (!response) {
      fail?.({ message: t('app.default.error') });
    } else if (response.status < 200 || response.status >= 300) {
      fail?.(response?.data as IErrorResponse);
    } else {
      data?.(response?.data as T);
    }
  };

  return {
    request,
    requestWithCallback,
  };
};
